import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'react-bootstrap/Button';
// import ReCAPTCHA from 'react-google-recaptcha';
// import { RECAPTCHA_SITE_KEY } from '../../utils/constants';
import debounce from 'lodash/debounce';
import {
    useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import './style.scss';
import { captureMessage } from '@sentry/react';
import { useLocation } from 'react-router-dom';
import Password from '../Password';
import {
    userLogin as userLoginRules,
    password as passwordRules,
    email,
} from '../../utils/rules';
import TermsAndConditions from '../../termsandconditionsV2';
import VerificationCodeInput from '../VerificationCodeInput';
import { useDispatch } from 'react-redux';
import { setUserByValue } from '../../../redux/actions';

const PSignupForm = ({ setSignupFormStatus, setShow }) => {
    const { executeRecaptcha, } = useGoogleReCaptcha();
    const location = useLocation();
    const dispatch = useDispatch();

    const [code, setCode] = useState('');
    const [emailErrorOnSendCode, setEmailErrorOnSendCode] = useState('');
    const [showTerms, setShowTerms] = useState(false);
    const [agree, setAgree] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoginPage, setIsLoginPage] = useState(false);
    const [usernameError, setUsernameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [pwd1Style, setPwd1Style] = useState({
        borderRadius: 0,
        borderBottom: '1px solid #d7d7d7',
        background: 'transparent',
    });
    const [pwd2Style, setPwd2Style] = useState({
        borderRadius: 0,
        borderBottom: '1px solid #d7d7d7',
        background: 'transparent',
    });
    const [showVerificationCodeInput, setShowVerificationCodeInput] = useState(false);

    const handlePwd1Focus = () => {
        setPwd1Style({
            borderRadius: 0,
            background: 'transparent',
            borderBottom: '1px solid #66afe9',
        });
    };
    const handlePwd1Blur = (e) => {
        setPwd1Style({
            borderRadius: 0,
            borderBottom: '1px solid #d7d7d7',
            background: 'transparent',
        });
    };

    const getSignupSettings = async () => {
        await axios.get(`/api/settings`, {
            params: {
                only: 'signup_email_verify'
            }
        })
            .then(response => {
                const data = Object.assign({}, ...response.data);
                setShowVerificationCodeInput(parseInt(data.signup_email_verify) === 1 ?? false);
            });
    };

    const formik = useFormik({
        initialValues: {
            showVerificationCode: showVerificationCodeInput,
            login: '',
            email: '',
            pwd: '',
            agree: false,
            isHuman: '',
            code: '',
        },
        validationSchema: Yup.object({
            showVerificationCode: Yup.boolean(),
            login: Yup.string()
                .min(userLoginRules.minLength, `Mininum ${userLoginRules.minLength} characters`)
                .max(userLoginRules.maxLength, `Maximum ${userLoginRules.maxLength} characters`)
                .matches(
                    userLoginRules.regex,
                    'Only alpha numeric, -, and _ characters are allowed',
                )
                .notOneOf(userLoginRules.invalid, 'That username is not available')
                .required('Username is required'),
            email: Yup.string()
                .required('Email is required'),
            pwd: Yup.string()
                .min(8, 'Minimum 8 characters')
                .required('Password is required'),
            agree: Yup.mixed().oneOf(['true', true], 'You should agree to our terms and privacy policy'),
            isHuman: Yup.string().required('Verify that you are human'),
            code: Yup.number()
                .when("showVerificationCode", {
                    is: (showVerificationCode) => showVerificationCode === true,
                    then: Yup.number()
                        .typeError('Invalid email verification code')
                        .required('Email verification code required')
                })
        }),
        onSubmit: async (values, { setErrors }) => {
            setIsLoading(true);
            const referrer = localStorage.getItem('referrer');
            const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            await axios.post('/api/register', { ...values, referrer, tz: localTz })
                .then((response) => {
                    setIsLoading(false);
                    dispatch(setUserByValue(response.data.user));
                    localStorage.setItem('loggedin_user', response.data.user.id);
                    localStorage.setItem('loggedin_access_token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    localStorage.removeItem('referrer');

                    setSignupFormStatus(true);
                })
                .catch((error) => {
                    setIsLoading(false);
                    if (error.response && error.response.status === 422) {
                        if (!showVerificationCodeInput) {
                            const codeError = Object.keys(error.response.data.errors).filter((key) => key === 'code');

                            if (codeError.length) {
                                window.location.reload();
                            }
                        }
                        Object.keys(error.response.data.errors).forEach((key) => {
                            setErrors({
                                [key]: error.response.data.errors[key][0],
                            });
                        });
                    }
                });
        },
    });

    useEffect(() => {
        let isMounted = true;
        formik.setFieldValue('agree', agree);

        return () => isMounted = false;
    }, [agree]);

    useEffect(() => {
        formik.setFieldValue('showVerificationCode', showVerificationCodeInput);
    }, [showVerificationCodeInput]);

    useEffect(() => {
        setIsLoginPage(['/login', '/register'].includes(location.pathname));
        // send api call to get signup_email_verify
        getSignupSettings();
    }, []);

    const handleChange = (e) => {
        const { value, name } = e.target;
        formik.setFieldValue(name, value, false);

        if (name === 'login') {
            setUsernameError('');
        }

        if (name === 'email') {
            setEmailError('');
        }
    };

    const onSubmitWithReCAPTCHA = async (e) => {
        e.preventDefault();
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available in signup');
        } else {
            const token = await executeRecaptcha('signup');
            formik.setFieldValue('isHuman', token, false);

            if (showVerificationCodeInput) {
                formik.setFieldValue('code', code);
            }

            formik.handleSubmit();
        }
    };

    const checkUsername = useCallback(
        debounce(async (value) => {
            if (value === '') {
                return;
            }

            await axios.post('/api/validate/username', {
                login: value,
            }).then((response) => {
                formik.setErrors({ login: '' });
                setUsernameError('');
            }).catch((error) => {
                captureMessage(error);
                if (error.response && error.response.status === 422) {
                    setUsernameError(error.response.data.errors.login[0]);
                    formik.setErrors({ login: error.response.data.errors.login[0] });
                }
            });
        }, 500),
        [],
    );

    const checkEmail = useCallback(
        debounce(async (value) => {
            if (value === '') {
                return;
            }

            await axios.post('/api/validate/email', {
                email: value,
            }).then((response) => {
                formik.setErrors({ email: '' });
                setEmailError('');
            }).catch((error) => {
                captureMessage(error);
                if (error.response && error.response.status === 422) {
                    setEmailError(error.response.data.errors.email[0]);
                    formik.setErrors({ email: error.response.data.errors.email[0] });
                }
            });
        }, 500),
        [],
    );

    const handleFocusOut = (e) => {
        const { name, value } = e.target;

        if (name === 'login') {
            checkUsername(value);
        }

        if (name === 'email') {
            checkEmail(value);
        }
    };

    return (
        <div className="signup__form">
            <form onSubmit={onSubmitWithReCAPTCHA}>
                <input
                    name="signup_email_verify"
                    type="hidden"
                    value={formik.values.showVerificationCode}
                />
                <div className={`form-group ${((formik.errors.login && formik.touched.login) || (usernameError !== '')) ? ' has-error' : ''}`}>
                    <input
                        name="login"
                        id="signup-login"
                        type="text"
                        placeholder="Username"
                        className={`form-control${formik.errors.login && formik.touched.login ? ' is-invalid' : ''}`}
                        onChange={handleChange}
                        onBlur={handleFocusOut}
                        value={formik.values.login}
                        autoComplete="off"
                    />
                    {(usernameError !== '') ? <div className="invalid-feedback">{usernameError}</div> : null}
                    {(formik.errors.login && formik.touched.login && (usernameError === '')) ? <div className="invalid-feedback">{formik.errors.login}</div> : null}
                </div>

                <div className={`form-group${formik.errors.email && formik.touched.email ? ' has-error' : ''}`}>
                    <input
                        name="email"
                        id="email"
                        type="email"
                        placeholder="Email"
                        className={`form-control${((formik.errors.email && formik.touched.email) || (emailError !== '')) ? ' is-invalid' : ''}`}
                        onBlur={handleFocusOut}
                        onChange={handleChange}
                        value={formik.values.email}
                        autoComplete="off"
                    />
                    {
                        emailErrorOnSendCode
                            ? <div className="invalid-feedback">{emailErrorOnSendCode}</div>
                            : (
                                emailError !== ''
                                    ? <div className="invalid-feedback">{emailError}</div>
                                    : (
                                        formik.errors.email && formik.touched.email && (emailError === ''))
                                        ? <div className="invalid-feedback">{formik.errors.email}</div>
                                        : null
                            )
                    }
                </div>

                {
                    showVerificationCodeInput &&
                    <div className={`form-group ${formik.errors.code && formik.touched.code ? 'has-error' : ''}`}>
                        <VerificationCodeInput
                            toVerify={formik.values.email}
                            executeRecaptcha={executeRecaptcha}
                            code={code}
                            setCode={setCode}
                            emailError={emailError ?? formik.errors.email}
                            verificationError={formik.errors.code}
                            hasEmail={formik.values.email.length}
                            // emailErrorOnSendCode={emailErrorOnSendCode}
                            setEmailErrorOnSendCode={setEmailErrorOnSendCode}
                        />
                    </div>
                }

                <div className={`form-group${formik.errors.pwd && formik.touched.pwd ? ' has-error' : ''}`}>
                    <Password
                        name="pwd"
                        id="pwd"
                        placeholder="Password"
                        customClass={`${formik.errors.pwd && formik.touched.pwd ? ' is-invalid' : ''}`}
                        onBlur={(e) => {
                            formik.handleBlur(e);
                            handlePwd1Blur();
                        }}
                        onFocus={handlePwd1Focus}
                        onChange={handleChange}
                        value={formik.values.pwd}
                        bottomOnly
                        preicon={false}
                        style={{
                            ...(isLoginPage && { borderRadius: '32px 0 0 32px' }),
                        }}
                        actionStyle={
                            isLoginPage
                                ? {
                                    borderRadius: '0 32px 32px 0',
                                    border: '1px solid #333',
                                    background: '#1a1a1a',
                                    color: '#fcfcfc',
                                }
                                : pwd1Style
                        }
                        postIcon
                        autoComplete="new-password"
                    />
                    {formik.errors.pwd && formik.touched.pwd ? <div className="invalid-feedback">{formik.errors.pwd}</div> : null}
                </div>

                <div className="chk-terms-con">
                    <div className={`singup__terms check-rem signup pt-2 pb-1${agree ? ' agree' : ''}${formik.errors.agree && formik.touched.agree ? ' has-error' : ''}`}>
                        <input name="agree" id="check-1" type="checkbox" className="chkboxTerm" checked={agree} onChange={() => setShowTerms(true)} />
                        <label
                            htmlFor="check-1"
                            style={
                                isLoginPage
                                    ? { color: '#fcfcfc' }
                                    : { color: '#5f5f5f' }
                            }
                        >
                            <span>
                                You agree to our
                                {' '}
                                <a href="/terms/" target="_blank" rel="noreferrer"><b>Terms</b></a>
                                {' '}
                                &
                                {' '}
                                <a href="/privacy-policy" target="_blank" rel="noreferrer"><b>Privacy Policy.</b></a>
                            </span>
                        </label>
                    </div>
                </div>
                {
                    formik.errors.agree && formik.touched.agree ? (
                        <div className="su-bottom-alert mb-4" style={{ display: 'block', color: '#e3342f', fontSize: '80%' }}>
                            {formik.errors.agree}
                        </div>
                    ) : null
                }
                <div className="text-center signup-button-wrapper">
                    <Button
                        type={'submit'}
                        variant="info"
                        disabled={isLoading}
                        className="btn theme-btn theme-btn-2 mb-2 btn-block"
                    >
                        {isLoading ? 'Submitting' : 'Sign Up'}
                    </Button>
                </div>
            </form>
            <TermsAndConditions show={showTerms} setShow={setShowTerms} setAgree={setAgree} />
        </div>
    );
};

export default PSignupForm;
